import { type ChangeEvent, useId } from "react";
import { type Control, Controller } from "react-hook-form";

import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import type { AdminSetting } from "@/queries/adminSettings";

import type { SettingFormValues } from "./settingSchema";

interface SettingValueInputProps {
  setting: AdminSetting;
  control: Control<SettingFormValues>;
  disabled?: boolean;
  inputId?: string;
}

/**
 * Renders the editable value for a non-secret setting, dispatching on
 * `value_type`. Secrets are handled in `ReplaceSecretDialog`.
 */
export function SettingValueInput({
  setting,
  control,
  disabled,
  inputId,
}: SettingValueInputProps) {
  const fallbackId = useId();
  const id = inputId ?? fallbackId;

  if (setting.value_type === "bool") {
    return (
      <Controller
        control={control}
        name="value"
        render={({ field }) => (
          <Switch
            id={id}
            checked={field.value === true}
            onCheckedChange={(checked) => field.onChange(checked)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (setting.value_type === "int" || setting.value_type === "float") {
    return (
      <Controller
        control={control}
        name="value"
        render={({ field }) => (
          <Input
            id={id}
            type="number"
            inputMode={setting.value_type === "int" ? "numeric" : "decimal"}
            step={setting.value_type === "int" ? 1 : "any"}
            value={toInputString(field.value)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              if (raw === "") {
                field.onChange(null);
              } else {
                const parsed = Number(raw);
                field.onChange(Number.isNaN(parsed) ? raw : parsed);
              }
            }}
            disabled={disabled}
          />
        )}
      />
    );
  }

  if (setting.value_type === "decimal") {
    return (
      <Controller
        control={control}
        name="value"
        render={({ field }) => (
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            value={toInputString(field.value)}
            onChange={(e) => field.onChange(e.target.value)}
            disabled={disabled}
          />
        )}
      />
    );
  }

  // str (or unknown text type)
  return (
    <Controller
      control={control}
      name="value"
      render={({ field }) => (
        <Input
          id={id}
          type="text"
          value={toInputString(field.value)}
          onChange={(e) => field.onChange(e.target.value)}
          disabled={disabled}
        />
      )}
    />
  );
}

function toInputString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}
